import { Box, Chip, Collapse, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { CloudUpload, Delete as DeleteIcon, ExpandLess, ExpandMore, InfoOutlined } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".zip",
  ".jpg",
  ".apa",
  ".mer",
  ".acd",
  ".txt",
  ".optix",
  ".bmp",
  ".png",
  ".xml",
  ".svg",
  ".csv",
  ".cs",
  ".json",
  ".props",
  ".cache",
  ".pdb",
  ".dll",
  ".up2date",
  ".editorconfig",
  ".yaml",
  ".mp4",
  ".sqlite",
  ".db",
  ".dbold",
  ".bie",
  ".db-shm",
  ".db-wal",
  ".bin",
];

const ALLOWED_EXTENSIONS_LABEL = ALLOWED_EXTENSIONS.join(", ");
const ACCEPT = {
  "application/pdf": [],
  "application/msword": [],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
  "application/vnd.ms-excel": [],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
  "application/zip": [],
  "application/x-zip-compressed": [],
  "text/csv": [],
  "image/png": [],
  "image/jpeg": [],
  "image/jpg": [],
  ".zip": [],
  ...Object.fromEntries(ALLOWED_EXTENSIONS.map((ext) => [ext, []])),
};

const FileDropZone = ({
  setParentData,
  maxFiles = 1,
  maxTotalSizeLabel = "1 GB total",
  files,
  showSelectedFiles = true,
}) => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState("");
  const [formData, setFormData] = useState({ files: [] });
  const [showAllowedTypes, setShowAllowedTypes] = useState(false);

  useEffect(() => {
    if (alert !== "") {
      Swal.fire({ icon: "warning", text: alert, confirmButtonText: "Okay" });
    }
  }, [alert]);

  useEffect(() => {
    if (Array.isArray(files)) {
      setFormData((prev) => ({ ...prev, files }));
    }
  }, [files]);

  const getFileExtension = (name = "") => {
    const dotIndex = String(name).lastIndexOf(".");
    if (dotIndex < 0) return "";
    return String(name).slice(dotIndex).toLowerCase();
  };

  const isAllowedExtension = (name = "") => {
    const ext = getFileExtension(name);
    return ALLOWED_EXTENSIONS.includes(ext);
  };

  const getFileKey = (file) =>
    [
      String(file?.name || "").trim().toLowerCase(),
      Number(file?.size) || 0,
      Number(file?.lastModified) || 0,
    ].join("|");

  const mergeUniqueFiles = (existingFiles = [], incomingFiles = []) => {
    const next = [];
    const seen = new Set();
    [...(Array.isArray(existingFiles) ? existingFiles : []), ...(Array.isArray(incomingFiles) ? incomingFiles : [])].forEach((file) => {
      if (!file) return;
      const key = getFileKey(file);
      if (seen.has(key)) return;
      seen.add(key);
      next.push(file);
    });
    return next;
  };

  const onDropAccepted = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const incoming = acceptedFiles;
    let next = mergeUniqueFiles(formData.files, incoming);
    if (maxFiles && next.length > maxFiles) {
      next = next.slice(0, maxFiles);
      Swal.fire({ icon: "info", text: `${t('GENERAL.FILES_ALLOWED').replace('file(s)', maxFiles + ' file(s)')}`, confirmButtonText: "Okay" });
    }
    setFormData((prev) => ({ ...prev, files: next }));
    if (setParentData) {
      setParentData((prev) => {
        const prevFiles = Array.isArray(prev?.files) ? prev.files : [];
        const merged = mergeUniqueFiles(prevFiles, incoming);
        const finalFiles = maxFiles && merged.length > maxFiles ? merged.slice(0, maxFiles) : merged;
        return { ...prev, files: finalFiles };
      });
    }
    setAlert("");
  };

  const onDropRejected = (fileRejections) => {
    const names = (fileRejections || []).map((r) => r.file?.name).filter(Boolean).join(", ");
    const msg = names
      ? `${t('GENERAL.UNSUPPORTED_FILE_TYPES')} ${names}. Allowed extensions: ${ALLOWED_EXTENSIONS_LABEL}`
      : `Unsupported file type. Allowed extensions: ${ALLOWED_EXTENSIONS_LABEL}`;
    Swal.fire({ icon: "warning", text: msg, confirmButtonText: "Okay" });
    setAlert(msg);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    validator: (file) => {
      if (!isAllowedExtension(file?.name)) {
        return {
          code: "file-invalid-type",
          message: `Allowed extensions: ${ALLOWED_EXTENSIONS_LABEL}`,
        };
      }
      return null;
    },
    multiple: maxFiles !== 1,
    onDropAccepted,
    onDropRejected,
    useFsAccessApi: false,
  });

  const handleDeleteFile = (indexToDelete) => {
    const updatedFiles = formData.files.filter((_, index) => index !== indexToDelete);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
    if (setParentData) setParentData((prev) => ({ ...prev, files: updatedFiles }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Upload Files
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          border: "1.5px dashed #d7d7d7",
          borderRadius: 1.5,
          padding: 3,
          textAlign: "center",
          cursor: "pointer",
          width: "100%",
          minHeight: "150px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          backgroundColor: isDragActive ? "#f4f8fb" : "#fafafa",
          "&:hover": {
            border: (theme) => `1.5px dashed ${theme.palette.primary.main}`,
            color: (theme) => theme.palette.primary.main,
          },
        }}
      >
        <input
          {...getInputProps({
            onClick: (e) => {
              e.target.value = null;
            },
          })}
        />
        <CloudUpload sx={{ fontSize: 40, color: "text.secondary" }} />
        <Typography sx={{ fontWeight: 600 }}>
          {isDragActive ? "Drop files to upload" : "Click or drag files to upload"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {maxFiles === 1 ? "Supports one file" : "Supports multiple files"}
        </Typography>
        {maxFiles !== 1 && (
          <Typography variant="caption" color="text.secondary">
            Max {maxTotalSizeLabel}
          </Typography>
        )}
      </Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setShowAllowedTypes((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setShowAllowedTypes((prev) => !prev);
          }
        }}
        sx={{
          mt: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          color: "#0f5f83",
          backgroundColor: "#eef9fe",
          cursor: "pointer",
          outline: "none",
          "&:focus-visible": {
            boxShadow: "0 0 0 2px rgba(3, 169, 244, 0.35)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <InfoOutlined fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Allowed file types and max size
          </Typography>
        </Box>
        {showAllowedTypes ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </Box>
      <Collapse in={showAllowedTypes} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1.5,
            backgroundColor: "#e4f7ff",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1, color: "#0f5f83" }}>
            Only the following file types can be uploaded (Max {maxTotalSizeLabel}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {ALLOWED_EXTENSIONS.map((ext) => (
              <Chip key={ext} label={ext} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      </Collapse>
      {showSelectedFiles && formData.files && formData.files.length > 0 && (
        <Box sx={{ width: "100%", mt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Selected Files
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {formData.files.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  width: "100%",
                  minHeight: 48,
                  border: "1px solid #ededed",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 0,
                    pr: 1.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                <IconButton
                  aria-label={`Remove ${file.name}`}
                  onClick={() => handleDeleteFile(index)}
                  size="small"
                  color="error"
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileDropZone;
